'use client';

import { useEffect, useState } from 'react';
import type {
  SignalCategoryType,
  SignalStatusType,
} from '@terra/shared-schemas-orchestrator';
import {
  useFindingSearchContext,
  type SearchSignalResult,
} from '../finding-search';

export type ResolvedSignalDetails = {
  title?: string;
  url?: string;
  method?: string;
  endpointId?: string;
  harEntryId?: string;
  status?: SignalStatusType | null;
  category?: SignalCategoryType;
  isEndpointEditable?: boolean;
};

export function useResolvedSignalDetails({
  signalId,
  title,
  enabled = true,
}: {
  signalId?: string;
  title?: string | null;
  enabled?: boolean;
}): ResolvedSignalDetails {
  const titleFromArgs = title?.trim() || undefined;
  const { onGetSignalById } = useFindingSearchContext();
  const [fetchedSignal, setFetchedSignal] = useState<
    SearchSignalResult | null | undefined
  >(undefined);

  useEffect(() => {
    if (!enabled || !signalId || !onGetSignalById) {
      setFetchedSignal(null);
      return;
    }

    let cancelled = false;
    setFetchedSignal(undefined);

    Promise.resolve(onGetSignalById(signalId))
      .then((signal) => {
        if (cancelled) return;
        setFetchedSignal(signal);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Failed to resolve signal details', error);
        setFetchedSignal(null);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, signalId, onGetSignalById]);

  const isEndpointEditable =
    fetchedSignal === undefined
      ? undefined
      : fetchedSignal !== null &&
        fetchedSignal.origin === 'CO_PILOT' &&
        fetchedSignal.isAppWide !== true;

  return {
    title: titleFromArgs ?? (fetchedSignal?.label?.trim() || undefined),
    url: fetchedSignal?.url,
    method: fetchedSignal?.method,
    endpointId: fetchedSignal?.endpointId,
    harEntryId: fetchedSignal?.harEntryId,
    status:
      fetchedSignal == null
        ? undefined
        : ((fetchedSignal.status as SignalStatusType | null | undefined) ??
          null),
    category: fetchedSignal?.category as SignalCategoryType | undefined,
    isEndpointEditable,
  };
}

export function useResolvedSignalDisplayTitle({
  signalId,
  title,
  enabled = true,
}: {
  signalId?: string;
  title?: string | null;
  enabled?: boolean;
}): string | undefined {
  const titleFromArgs = title?.trim() || undefined;
  const details = useResolvedSignalDetails({
    signalId,
    title,
    enabled: enabled && !titleFromArgs,
  });
  return titleFromArgs ?? details.title;
}
